import React, { useEffect, useState, useContext } from "react";
import Sidebar from "../../../components/layout/Sidebar";
import Navbar from "../../../components/layout/Navbar";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { 
  getActivitiesByProcess, 
  getBehaviourTypes, 
  saveActivity 
} from "../../../services/reference.service";
import { 
  ChevronLeft, 
  Plus, 
  ListChecks, 
  Hash, 
  Settings2, 
  Repeat, 
  Edit3, 
  Loader2,
  AlertCircle
} from "lucide-react";

export default function Activities() {
  const { processId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [activities, setActivities] = useState([]);
  const [behaviours, setBehaviours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [newActivity, setNewActivity] = useState({
    ActivityId: null,
    ProcessId: Number(processId),
    ActivityCode: "",
    ActivityName: "",
    BehaviourTypesId: "",
    IsRepeatable: false,
    UserCreated: user?.userName || "system",
  });

  useEffect(() => {
    loadBehaviourTypes();
    loadActivities();
  }, [processId]);

  const loadBehaviourTypes = async () => {
    const token = localStorage.getItem("token") || "";
    const data = await getBehaviourTypes(token);
    setBehaviours(data || []);
  };

  const loadActivities = async () => {
    setLoading(true);
    const token = localStorage.getItem("token") || "";
    const data = await getActivitiesByProcess(token, processId);
    setActivities(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewActivity((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const saveNew = async () => {
    if (!newActivity.ActivityCode || !newActivity.ActivityName || !newActivity.BehaviourTypesId) {
      setErrorMsg("⚠️ Completa los campos obligatorios para continuar.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    const token = localStorage.getItem("token") || "";
    const result = await saveActivity(token, newActivity);
    setSaving(false);

    if (result.success === 1) {
      const insertedId = result.data;
      const behaviour = behaviours.find((b) => b.id == newActivity.BehaviourTypesId);
      
      setActivities((prev) => [
        ...prev,
        {
          activityId: insertedId,
          activityCode: newActivity.ActivityCode,
          activityName: newActivity.ActivityName,
          behaviourTypeName: behaviour?.description || "",
          behaviourTypeCode: behaviour?.code || "",
          isRepeatable: newActivity.IsRepeatable,
        },
      ]);

      setNewActivity({
        ActivityId: null,
        ProcessId: Number(processId),
        ActivityCode: "",
        ActivityName: "",
        BehaviourTypesId: "",
        IsRepeatable: false,
        UserCreated: user?.userName || "system",
      });
    } else {
      setErrorMsg("❌ No se pudo guardar la actividad. Revisa la conexión.");
    }
  };

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden text-slate-900">
      <Sidebar />

      <main className="flex-1 flex flex-col overflow-hidden">
        <Navbar />

        {/* HEADER */}
        <div className="px-8 py-5 bg-white border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate("/processes")}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-900"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <div>
              <span className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">Configuración de Pasos</span>
              <h2 className="text-2xl font-black text-slate-900 leading-none">Actividades del Proceso #{processId}</h2>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-10">
          
          {/* FORMULARIO DE CREACIÓN RÁPIDA */}
          <section className="max-w-5xl mx-auto">
            <div className="bg-white rounded-[2rem] shadow-xl shadow-slate-200/50 border border-slate-200 overflow-hidden">
              <div className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                    <Plus className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">Nueva Actividad</h3>
                </div>

                {errorMsg && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl flex items-center gap-3 text-sm font-bold animate-in fade-in zoom-in duration-300">
                    <AlertCircle className="w-4 h-4" /> {errorMsg}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end font-medium">
                  {/* Código */}
                  <div className="md:col-span-2 space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      <Hash className="w-3 h-3" /> Código
                    </label>
                    <input
                      type="text"
                      name="ActivityCode"
                      value={newActivity.ActivityCode}
                      onChange={handleChange}
                      placeholder="A001"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    />
                  </div>

                  {/* Nombre */}
                  <div className="md:col-span-4 space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      <ListChecks className="w-3 h-3" /> Descripción de la tarea
                    </label>
                    <input
                      type="text"
                      name="ActivityName"
                      value={newActivity.ActivityName}
                      onChange={handleChange}
                      placeholder="Ej. Inspección de válvulas"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    />
                  </div>

                  {/* Comportamiento */}
                  <div className="md:col-span-3 space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                      <Settings2 className="w-3 h-3" /> Comportamiento
                    </label>
                    <select
                      name="BehaviourTypesId"
                      value={newActivity.BehaviourTypesId}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    >
                      <option value="">Seleccione...</option>
                      {behaviours.map((b) => (
                        <option key={b.id} value={b.id}>{b.code} — {b.description}</option>
                      ))}
                    </select>
                  </div>

                  {/* Repetible */}
                  <div className="md:col-span-2 flex items-center justify-center pb-3">
                    <label className="flex items-center gap-3 cursor-pointer group">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${newActivity.IsRepeatable ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-100 text-slate-300 border border-slate-200'}`}>
                        <Repeat className="w-5 h-5" />
                      </div>
                      <input
                        type="checkbox"
                        name="IsRepeatable"
                        checked={newActivity.IsRepeatable}
                        onChange={handleChange}
                        className="hidden"
                      />
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-tighter">Repetible</span>
                    </label>
                  </div>

                  {/* Botón Guardar */}
                  <div className="md:col-span-1 flex justify-end pb-1">
                    <button
                      onClick={saveNew}
                      disabled={saving}
                      className={`p-3 rounded-xl transition-all shadow-lg active:scale-95 ${saving ? 'bg-slate-200' : 'bg-slate-900 hover:bg-blue-600 text-white shadow-slate-900/10'}`}
                    >
                      {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Plus className="w-6 h-6" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* LISTADO DE ACTIVIDADES EXISTENTES */}
          <section className="max-w-6xl mx-auto">
            <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em] mb-4 ml-2">Pasos definidos en este proceso</h3>
            
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
              {loading ? (
                <div className="p-20 text-center">
                  <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
                  <p className="text-slate-400 font-bold tracking-widest">SINCRONIZANDO PASOS...</p>
                </div>
              ) : activities.length === 0 ? (
                <div className="p-20 text-center text-slate-400 italic">
                  No hay actividades registradas. Usa el formulario superior para añadir el primer paso.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="py-4 px-8 text-[10px] font-black text-slate-400 uppercase tracking-widest w-24">Cód.</th>
                        <th className="py-4 px-8 text-[10px] font-black text-slate-400 uppercase tracking-widest">Nombre de Actividad / Tarea</th>
                        <th className="py-4 px-8 text-[10px] font-black text-slate-400 uppercase tracking-widest">Comportamiento Asignado</th>
                        <th className="py-4 px-8 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {activities.map((a) => (
                        <tr key={a.activityId} className="group hover:bg-blue-50/30 transition-colors">
                          <td className="py-5 px-8 font-black text-blue-600 text-xs">{a.activityCode}</td>
                          <td className="py-5 px-8">
                            <span className="text-sm font-bold text-slate-700 block max-w-md break-words">
                              {a.activityName}
                            </span>
                          </td>
                          <td className="py-5 px-8">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black bg-slate-100 text-slate-500 px-2 py-1 rounded uppercase tracking-tighter">
                                {a.behaviourTypeCode}
                              </span>
                              <span className="text-xs font-medium text-slate-500">{a.behaviourTypeName}</span>
                            </div>
                          </td>
                          <td className="py-5 px-8 text-center">
                            <button
                              onClick={() => navigate(`/processes/${processId}/activities/edit/${a.activityId}`)}
                              className="inline-flex items-center gap-2 text-slate-400 hover:text-blue-600 font-bold text-xs transition-colors"
                            >
                              <Edit3 className="w-4 h-4" /> <span>Editar</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}